import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Copy, PenLine, Plus, Sparkles, Trash2 } from "lucide-react";
import { useDeferredValue, useEffect, useId, useState, type FormEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type * as S from "@shipyard/shared";
import { STORE_LOCALES } from "@shipyard/shared/constants";
import { ChipsInput } from "@/components/chips";
import { ConfirmDialog, PlatformIcon, toastError } from "@/components/common";
import { Guide } from "@/components/guides";
import { KeystoreCard } from "@/components/keystore";
import { Badge, Button, Card, CardHeader, Field, Input, PageHeader, Select, Spinner, Switch, Textarea } from "@/components/ui";
import { http } from "@/lib/api";
import {
  keys,
  useApp,
  useAppleCredentials,
  useGitBranches,
  useGitProviders,
  useGitRepos,
  useGoogleCredentials,
  useLlmProviders,
  useMembers,
  useOrgMembers,
  useTestflightGroups,
} from "@/lib/queries";
import { cn } from "@/lib/utils";

interface FormState {
  name: string;
  iosEnabled: boolean;
  iosBundleId: string;
  iosAscAppId: string;
  appleCredentialId: string;
  iosTestflightGroups: string[];
  iosReleaseMode: S.IosReleaseMode;
  androidEnabled: boolean;
  androidPackage: string;
  googleCredentialId: string;
  androidRollout: number;
  llmProviderId: string;
  gitProviderId: string;
  gitRepo: string;
  gitBranch: string;
  locales: string[];
  changelogMode: S.ChangelogMode;
  changelogTemplate: string;
  changelogInstructions: string;
}

const EMPTY: FormState = {
  name: "",
  iosEnabled: false,
  iosBundleId: "",
  iosAscAppId: "",
  appleCredentialId: "",
  iosTestflightGroups: [],
  iosReleaseMode: "manual",
  androidEnabled: false,
  androidPackage: "",
  googleCredentialId: "",
  androidRollout: 100,
  llmProviderId: "",
  gitProviderId: "",
  gitRepo: "",
  gitBranch: "",
  locales: ["en-US"],
  changelogMode: "auto",
  changelogTemplate: "",
  changelogInstructions: "",
};

function fromApp(app: S.App): FormState {
  return {
    name: app.name,
    iosEnabled: !!app.iosBundleId,
    iosBundleId: app.iosBundleId ?? "",
    iosAscAppId: app.iosAscAppId ?? "",
    appleCredentialId: app.appleCredentialId ?? "",
    iosTestflightGroups: app.iosTestflightGroups,
    iosReleaseMode: app.iosReleaseMode,
    androidEnabled: !!app.androidPackage,
    androidPackage: app.androidPackage ?? "",
    googleCredentialId: app.googleCredentialId ?? "",
    androidRollout: Math.round(app.androidRollout * 100),
    llmProviderId: app.llmProviderId ?? "",
    gitProviderId: app.gitProviderId ?? "",
    gitRepo: app.gitRepo ?? "",
    gitBranch: app.gitBranch ?? "",
    locales: app.locales,
    changelogMode: app.changelogMode,
    changelogTemplate: app.changelogTemplate ?? "",
    changelogInstructions: app.changelogInstructions ?? "",
  };
}

const orNull = (v: string) => (v.trim() ? v.trim() : null);

function toBody(f: FormState) {
  const ios = f.iosEnabled;
  const android = f.androidEnabled;
  return {
    name: f.name.trim(),
    iosBundleId: ios ? orNull(f.iosBundleId) : null,
    iosAscAppId: ios ? orNull(f.iosAscAppId) : null,
    appleCredentialId: ios ? orNull(f.appleCredentialId) : null,
    iosTestflightGroups: ios ? f.iosTestflightGroups : [],
    iosReleaseMode: f.iosReleaseMode,
    androidPackage: android ? orNull(f.androidPackage) : null,
    googleCredentialId: android ? orNull(f.googleCredentialId) : null,
    androidRollout: Math.min(100, Math.max(1, f.androidRollout)) / 100,
    llmProviderId: orNull(f.llmProviderId),
    gitProviderId: orNull(f.gitProviderId),
    gitRepo: f.gitProviderId ? orNull(f.gitRepo) : null,
    gitBranch: f.gitProviderId ? orNull(f.gitBranch) : null,
    locales: f.locales,
    changelogMode: f.changelogMode,
    changelogTemplate: orNull(f.changelogTemplate),
    changelogInstructions: orNull(f.changelogInstructions),
  };
}

// ------------------------------------------------------------------ platform toggles

function PlatformToggle({ platform, enabled, onChange, title, description }: { platform: S.Platform; enabled: boolean; onChange: (v: boolean) => void; title: string; description: string }) {
  const id = useId();
  return (
    <label
      htmlFor={id}
      className={cn(
        "flex cursor-pointer items-start gap-3 rounded-lg border p-4 transition-colors",
        enabled ? "border-brand-500 bg-brand-50/60 dark:bg-sky-950/30" : "bg-panel hover:bg-muted/50",
      )}
    >
      <PlatformIcon platform={platform} className="mt-0.5 h-5 w-5" />
      <span className="flex-1">
        <span className="block font-medium">{title}</span>
        <span className="block text-xs text-fg-muted">{description}</span>
      </span>
      <Switch id={id} checked={enabled} onCheckedChange={onChange} />
    </label>
  );
}

// ------------------------------------------------------------------ release notes mode

const DEFAULT_TEMPLATE = "{{#each commits}}• {{subject}}\n{{/each}}";

function ModeCard({ mode, selected, onSelect }: { mode: S.ChangelogMode; selected: boolean; onSelect: () => void }) {
  const { t } = useTranslation();
  const Icon = { auto: Sparkles, copy_previous: Copy, manual: PenLine }[mode];
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={cn(
        "flex flex-col gap-1.5 rounded-lg border p-4 text-left transition-colors",
        selected ? "border-brand-500 bg-brand-50/60 ring-1 ring-brand-500 dark:bg-sky-950/30" : "bg-panel hover:bg-muted/50",
      )}
    >
      <span className="flex items-center gap-2 font-medium">
        <Icon className="h-4 w-4 text-brand-600" />
        {t(`appSettings.mode_${mode}`)}
      </span>
      <span className="text-xs text-fg-muted">{t(`appSettings.mode_${mode}_help`)}</span>
    </button>
  );
}

// ------------------------------------------------------------------ git pickers

function RepoPicker({ providerId, value, onChange }: { providerId: string; value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation();
  const listId = useId();
  const query = useDeferredValue(value);
  const repos = useGitRepos(providerId, query);
  return (
    <>
      <Input id="repo" list={listId} value={value} onChange={(e) => onChange(e.target.value)} placeholder="acme/mobile-app" className="font-mono" autoComplete="off" />
      <datalist id={listId}>
        {repos.data?.map((r) => (
          <option key={r.fullName} value={r.fullName}>
            {r.private ? "🔒" : ""} {r.defaultBranch ?? ""}
          </option>
        ))}
      </datalist>
      {repos.error && <p className="text-xs text-red-600">{(repos.error as Error).message}</p>}
      {repos.isFetching && <p className="text-xs text-fg-muted">{t("common.loading")}</p>}
    </>
  );
}

function BranchPicker({ appId, value, onChange }: { appId: string; value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation();
  const branches = useGitBranches(appId);
  if (branches.error) return <p className="text-xs text-red-600">{(branches.error as Error).message}</p>;
  return (
    <Select id="branch" value={value} onChange={(e) => onChange(e.target.value)} disabled={branches.isLoading}>
      <option value="">{t("appSettings.repoDefaultBranch")}</option>
      {branches.data?.branches.map((b) => (
        <option key={b.name} value={b.name}>
          {b.name}
        </option>
      ))}
    </Select>
  );
}

// ------------------------------------------------------------------ members

function Members({ appId }: { appId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const members = useMembers(appId);
  const orgMembers = useOrgMembers();
  const users = orgMembers.data?.map((m) => ({ id: m.userId, name: m.name, email: m.email, role: m.role }));
  const [draft, setDraft] = useState<{ userId: string; role: S.AppMemberRole }[] | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (members.data) setDraft(members.data.map((m) => ({ userId: m.userId, role: m.role })));
  }, [members.data]);
  if (!draft || !users) return <Spinner />;
  // Organization owners and admins already manage every app.
  const candidates = users.filter((u) => u.role === "member" && !draft.some((d) => d.userId === u.id));

  const save = async (next: typeof draft) => {
    setDraft(next);
    setSaving(true);
    try {
      await http.put(`/apps/${appId}/members`, { members: next });
      void qc.invalidateQueries({ queryKey: keys.members(appId) });
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader title={t("appSettings.members")} actions={saving && <Spinner />} />
      <div className="space-y-3 p-5">
        {draft.length === 0 && <p className="text-sm text-fg-muted">{t("appSettings.noMembers")}</p>}
        {draft.map((m) => {
          const u = users.find((x) => x.id === m.userId);
          return (
            <div key={m.userId} className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{u?.name}</p>
                <p className="truncate text-xs text-fg-muted">{u?.email}</p>
              </div>
              <Select className="w-36" value={m.role} onChange={(e) => save(draft.map((d) => (d.userId === m.userId ? { ...d, role: e.target.value as S.AppMemberRole } : d)))}>
                <option value="manager">{t("apps.roleManager")}</option>
                <option value="viewer">{t("apps.roleViewer")}</option>
              </Select>
              <Button variant="ghost" size="icon" onClick={() => save(draft.filter((d) => d.userId !== m.userId))} aria-label={t("common.delete")}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          );
        })}
        {candidates.length > 0 && (
          <Select value="" onChange={(e) => e.target.value && save([...draft, { userId: e.target.value, role: "manager" }])} className="max-w-xs" aria-label={t("appSettings.memberAdd")}>
            <option value="">+ {t("appSettings.memberAdd")}</option>
            {candidates.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name} ({u.email})
              </option>
            ))}
          </Select>
        )}
      </div>
    </Card>
  );
}

// ------------------------------------------------------------------ page

function Section({ title, children }: { title: ReactNode; children: ReactNode }) {
  return (
    <Card>
      <CardHeader title={title} />
      <div className="space-y-4 p-5">{children}</div>
    </Card>
  );
}

export function AppSettingsPage({ appId }: { appId?: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const isNew = !appId;
  const app = useApp(appId ?? "");
  const apple = useAppleCredentials();
  const google = useGoogleCredentials();
  const llm = useLlmProviders();
  const git = useGitProviders();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (app.data) setForm(fromApp(app.data));
  }, [app.data]);
  const saved = app.data;
  const tfGroups = useTestflightGroups(appId ?? "", !!saved?.iosAscAppId && !!saved.appleCredentialId && form.iosEnabled);

  if (!isNew && app.isLoading) return <Spinner />;
  const patch = (p: Partial<FormState>) => setForm((f) => ({ ...f, ...p }));
  const set = <K extends keyof FormState>(k: K) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: (typeof EMPTY[k] === "number" ? Number(e.target.value) : e.target.value) as FormState[K] }));
  const noPlatform = !form.iosEnabled && !form.androidEnabled;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (noPlatform) return;
    setSaving(true);
    try {
      if (isNew) {
        const created = await http.post<S.App>("/apps", toBody(form));
        toast.success(t("appSettings.created"));
        void qc.invalidateQueries({ queryKey: keys.apps });
        await navigate({ to: "/apps/$appId/settings", params: { appId: created.id } });
      } else {
        await http.patch<S.App>(`/apps/${appId}`, toBody(form));
        toast.success(t("common.saved"));
        void qc.invalidateQueries({ queryKey: keys.apps });
        void qc.invalidateQueries({ queryKey: ["apps", appId] });
      }
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const selectOptions = (items: { id: string; name: string }[] | undefined) => [
    <option key="" value="">
      — {t("common.none")} —
    </option>,
    ...(items ?? []).map((i) => (
      <option key={i.id} value={i.id}>
        {i.name}
      </option>
    )),
  ];

  const groupHint = (g: S.TestflightGroup) => (
    <Badge tone={g.isInternal ? "neutral" : "violet"}>{g.isInternal ? (g.allBuilds ? t("appSettings.groupInternalAuto") : t("appSettings.groupInternal")) : t("appSettings.groupExternal")}</Badge>
  );
  const missingTools = [
    !form.llmProviderId && t("appSettings.llmProvider"),
    !form.gitProviderId && t("appSettings.gitProvider"),
    form.gitProviderId && !form.gitRepo && t("appSettings.gitRepo"),
  ].filter((v): v is string => !!v);
  const gitSaved = !!saved?.gitProviderId && !!saved.gitRepo && saved.gitProviderId === form.gitProviderId && saved.gitRepo === form.gitRepo;

  return (
    <>
      {!isNew && (
        <Link to="/apps/$appId" params={{ appId }} className="mb-4 inline-flex items-center gap-1 text-sm text-fg-muted hover:text-fg">
          <ArrowLeft className="h-4 w-4" />
          {app.data?.name}
        </Link>
      )}
      <PageHeader title={isNew ? t("apps.new") : t("appSettings.title")} />
      <form onSubmit={submit} className="space-y-6">
        <Section title={t("appSettings.general")}>
          <Field label={t("common.name")} htmlFor="name">
            <Input id="name" required value={form.name} onChange={set("name")} />
          </Field>
          <div className="space-y-2">
            <p className="text-sm font-medium">{t("appSettings.platforms")}</p>
            <div className="grid gap-3 md:grid-cols-2">
              <PlatformToggle platform="ios" enabled={form.iosEnabled} onChange={(iosEnabled) => patch({ iosEnabled })} title={t("appSettings.ios")} description={t("appSettings.iosToggleHelp")} />
              <PlatformToggle
                platform="android"
                enabled={form.androidEnabled}
                onChange={(androidEnabled) => patch({ androidEnabled })}
                title={t("appSettings.android")}
                description={t("appSettings.androidToggleHelp")}
              />
            </div>
            <p className={cn("text-xs", noPlatform ? "text-red-600" : "text-fg-muted")}>{t("appSettings.platformsHelp")}</p>
          </div>
          <Field label={t("appSettings.locales")} help={t("appSettings.localesHelp")} htmlFor="locales">
            <ChipsInput
              id="locales"
              value={form.locales}
              onChange={(locales) => patch({ locales })}
              suggestions={STORE_LOCALES.map((l) => ({ value: l.code, label: l.label }))}
              placeholder="en-US"
            />
          </Field>
        </Section>

        {(form.iosEnabled || form.androidEnabled) && (
          <div className={cn("grid gap-6", form.iosEnabled && form.androidEnabled && "lg:grid-cols-2")}>
            {form.iosEnabled && (
              <Section title={<span className="flex items-center gap-2"><PlatformIcon platform="ios" />{t("appSettings.ios")}</span>}>
                <Field label={t("appSettings.bundleId")} htmlFor="bundle">
                  <Input id="bundle" required value={form.iosBundleId} onChange={set("iosBundleId")} placeholder="com.company.app" className="font-mono" />
                </Field>
                <Field label={t("appSettings.ascAppId")} help={t("appSettings.ascAppIdHelp")} htmlFor="ascid">
                  <Input id="ascid" value={form.iosAscAppId} onChange={set("iosAscAppId")} inputMode="numeric" placeholder="1234567890" className="font-mono" />
                </Field>
                <Field label={t("appSettings.appleCredential")} htmlFor="applecred">
                  <Select id="applecred" value={form.appleCredentialId} onChange={set("appleCredentialId")}>
                    {selectOptions(apple.data)}
                  </Select>
                </Field>
                {!apple.data?.length && <Guide kind="apple" />}
                <Field label={t("appSettings.testflightGroups")} help={isNew || !tfGroups.data ? t("appSettings.testflightGroupsHelpSave") : t("appSettings.testflightGroupsHelp")} htmlFor="groups">
                  <ChipsInput
                    id="groups"
                    value={form.iosTestflightGroups}
                    onChange={(iosTestflightGroups) => patch({ iosTestflightGroups })}
                    suggestions={tfGroups.data?.map((g) => ({ value: g.name, hint: groupHint(g) }))}
                    loading={tfGroups.isFetching}
                  />
                  {tfGroups.error && <p className="text-xs text-red-600">{(tfGroups.error as Error).message}</p>}
                </Field>
                <Field label={t("appSettings.releaseMode")} htmlFor="relmode">
                  <Select id="relmode" value={form.iosReleaseMode} onChange={set("iosReleaseMode")}>
                    <option value="manual">{t("build.releaseModeManual")}</option>
                    <option value="after_approval">{t("build.releaseModeAuto")}</option>
                  </Select>
                </Field>
              </Section>
            )}
            {form.androidEnabled && (
              <Section title={<span className="flex items-center gap-2"><PlatformIcon platform="android" />{t("appSettings.android")}</span>}>
                <Field label={t("appSettings.package")} help={t("appSettings.packageHelp")} htmlFor="pkg">
                  <Input id="pkg" required value={form.androidPackage} onChange={set("androidPackage")} placeholder="com.company.app" className="font-mono" />
                </Field>
                <Field label={t("appSettings.googleCredential")} htmlFor="googlecred">
                  <Select id="googlecred" value={form.googleCredentialId} onChange={set("googleCredentialId")}>
                    {selectOptions(google.data)}
                  </Select>
                </Field>
                {!google.data?.length && <Guide kind="google" />}
                <Field label={t("appSettings.rollout")} htmlFor="rollout">
                  <Input id="rollout" type="number" min={1} max={100} value={form.androidRollout} onChange={set("androidRollout")} />
                </Field>
              </Section>
            )}
          </div>
        )}

        <Section title={t("appSettings.notesFill")}>
          <p className="-mt-1 text-sm text-fg-muted">{t("appSettings.notesFillHelp")}</p>
          <div className="grid gap-3 md:grid-cols-3" role="radiogroup" aria-label={t("appSettings.notesFill")}>
            {(["auto", "copy_previous", "manual"] as const).map((mode) => (
              <ModeCard key={mode} mode={mode} selected={form.changelogMode === mode} onSelect={() => patch({ changelogMode: mode })} />
            ))}
          </div>
          {form.changelogMode === "auto" && (
            <>
              {missingTools.length > 0 && (
                <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                  {t("appSettings.autoMissing", { tools: missingTools.join(", ") })}{" "}
                  <a href="#tools" className="font-medium underline">
                    {t("appSettings.toolsTitle")}
                  </a>
                </p>
              )}
              <Field
                label={<>{t("appSettings.template")} <span className="font-normal text-fg-muted">({t("common.optional")})</span></>}
                help={t("appSettings.templateHelp")}
                htmlFor="tpl"
              >
                <Textarea id="tpl" rows={4} value={form.changelogTemplate} onChange={set("changelogTemplate")} placeholder={DEFAULT_TEMPLATE} className="font-mono text-xs" />
                {form.changelogTemplate && (
                  <button type="button" className="text-xs text-brand-600 hover:underline" onClick={() => patch({ changelogTemplate: "" })}>
                    {t("appSettings.useDefaultTemplate")}
                  </button>
                )}
              </Field>
            </>
          )}
        </Section>

        <div id="tools" className="scroll-mt-6">
          <Section title={t("appSettings.toolsTitle")}>
            <p className="-mt-1 text-sm text-fg-muted">{t("appSettings.toolsHelp")}</p>
            <div className="grid gap-4 md:grid-cols-2">
              <Field label={t("appSettings.llmProvider")} help={t("appSettings.llmProviderHelp")} htmlFor="llm">
                <Select id="llm" value={form.llmProviderId} onChange={set("llmProviderId")}>
                  {selectOptions(llm.data)}
                </Select>
              </Field>
              <Field label={t("appSettings.gitProvider")} help={t("appSettings.gitProviderHelp")} htmlFor="git">
                <Select id="git" value={form.gitProviderId} onChange={set("gitProviderId")}>
                  {selectOptions(git.data)}
                </Select>
              </Field>
              {form.gitProviderId && (
                <Field label={t("appSettings.gitRepo")} help={t("appSettings.gitRepoHelp")} htmlFor="repo">
                  <RepoPicker providerId={form.gitProviderId} value={form.gitRepo} onChange={(gitRepo) => patch({ gitRepo, gitBranch: "" })} />
                </Field>
              )}
              {form.gitProviderId && form.gitRepo && (
                <Field label={t("appSettings.gitBranch")} help={gitSaved ? t("appSettings.gitBranchHelp") : t("appSettings.gitBranchHelpSave")} htmlFor="branch">
                  {gitSaved ? <BranchPicker appId={appId!} value={form.gitBranch} onChange={(gitBranch) => patch({ gitBranch })} /> : <Input id="branch" value={form.gitBranch} onChange={set("gitBranch")} placeholder="main" className="font-mono" />}
                </Field>
              )}
            </div>
            {form.gitProviderId && <p className="rounded-md bg-muted/60 px-3 py-2 text-xs text-fg-muted">{t("appSettings.comparisonHelp")}</p>}
            <Field label={t("appSettings.instructions")} help={t("appSettings.instructionsHelp")} htmlFor="instr">
              <Textarea id="instr" rows={3} value={form.changelogInstructions} onChange={set("changelogInstructions")} />
            </Field>
          </Section>
        </div>

        <div className="flex items-center justify-end gap-3">
          {noPlatform && <span className="text-sm text-red-600">{t("appSettings.platformsHelp")}</span>}
          <Button type="submit" loading={saving} disabled={noPlatform || form.locales.length === 0}>
            {isNew ? (
              <>
                <Plus className="h-4 w-4" />
                {t("common.create")}
              </>
            ) : (
              t("common.save")
            )}
          </Button>
        </div>
      </form>

      {!isNew && (
        <div className="mt-8 space-y-6">
          {saved?.androidPackage && <KeystoreCard appId={appId} canManage />}
          <Members appId={appId} />
          <Card className="border-red-200 dark:border-red-900">
            <CardHeader
              title={t("appSettings.deleteApp")}
              description={t("appSettings.deleteAppHelp")}
              actions={
                <ConfirmDialog
                  danger
                  title={t("appSettings.deleteApp")}
                  description={t("common.irreversible")}
                  confirmLabel={t("common.delete")}
                  onConfirm={async () => {
                    await http.del(`/apps/${appId}`);
                    void qc.invalidateQueries({ queryKey: keys.apps });
                    await navigate({ to: "/apps" });
                  }}
                  trigger={(open) => (
                    <Button variant="danger" onClick={open}>
                      {t("common.delete")}
                    </Button>
                  )}
                />
              }
            />
          </Card>
        </div>
      )}
    </>
  );
}
